if (!customElements.get('locale-switcher')) {
  customElements.define(
    'locale-switcher',
    class LocaleSwitcher extends HTMLElement {
      constructor() {
        super();
        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        this.panel = this.querySelector('.locale-switcher__panel');
        this.applyButton = this.querySelector('[data-locale-apply]');
        this.header = document.querySelector('.header-wrapper');
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        if (!this.details || !this.summary) return;

        this.summary.setAttribute('role', 'button');
        this.summary.setAttribute('aria-expanded', this.details.hasAttribute('open') ? 'true' : 'false');

        this.bindEvents();
        this.observeTranscyLanguage();
      }

      bindEvents() {
        this.summary.addEventListener('click', this.onSummaryClick.bind(this));
        this.details.addEventListener('toggle', this.onToggle.bind(this));
        this.addEventListener('keyup', this.onKeyUp);
        this.addEventListener('keydown', this.onKeyDown);

        if (this.applyButton) {
          this.applyButton.addEventListener('click', this.onApplyClick.bind(this));
        }

        // Close buttons inside the panel (exclude localization / apply controls)
        this.querySelectorAll(
          'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not([data-locale-apply])'
        ).forEach((button) => {
          if (button.closest('.locale-switcher__language')) return;
          button.addEventListener('click', this.onCloseButtonClick.bind(this));
        });
      }

      onSummaryClick(event) {
        // Native details toggles open; sync aria after the toggle event
        event.stopPropagation();
      }

      onToggle() {
        const isOpen = this.details.hasAttribute('open');
        this.summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (this.header) {
          this.header.preventHide = isOpen;
        }

        if (isOpen) {
          // Defer so the opening click does not immediately close the panel
          requestAnimationFrame(() => {
            document.addEventListener('click', this.onDocumentClick);
          });
        } else {
          document.removeEventListener('click', this.onDocumentClick);
          this.closeInnerCurrencyPanel();
        }
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.close();
      }

      onKeyDown(event) {
        if (event.key !== 'Tab') return;
        if (!this.details.hasAttribute('open') || !this.panel) return;

        const items = this.getPanelFocusable();
        if (!items.length) return;

        const active = document.activeElement;
        const index = items.indexOf(active);

        if (active === this.summary && !event.shiftKey) {
          event.preventDefault();
          items[0].focus();
          return;
        }

        if (index === -1) return;

        if (event.shiftKey) {
          event.preventDefault();
          if (index === 0) this.summary.focus();
          else items[index - 1].focus();
          return;
        }

        event.preventDefault();
        if (index < items.length - 1) {
          items[index + 1].focus();
        } else {
          items[0].focus();
        }
      }

      getPanelFocusable() {
        const selector = [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled]):not([type="hidden"])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(',');

        return [...this.panel.querySelectorAll(selector)].filter((el) => this.isFocusable(el));
      }

      isFocusable(el) {
        if (el.closest('[hidden]')) return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        // Keep close in the Tab cycle even when visually clipped on desktop
        if (el.hasAttribute('data-locale-close')) return true;
        const style = window.getComputedStyle(el);
        if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
        return el.getClientRects().length > 0;
      }

      onKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        if (!this.details.hasAttribute('open')) return;

        const currencyPanel = this.querySelector('.disclosure__list-wrapper:not([hidden])');
        if (currencyPanel) {
          const localizationForm = this.querySelector('localization-form');
          localizationForm?.hidePanel?.();
          event.stopPropagation();
          return;
        }

        this.close();
        this.summary.focus();
        event.stopPropagation();
      }

      onCloseButtonClick(event) {
        event.preventDefault();
        this.close();
      }

      onApplyClick(event) {
        event.preventDefault();
        const form = this.querySelector('form.localization-form');
        if (form) form.submit();
      }

      closeInnerCurrencyPanel() {
        const localizationForm = this.querySelector('localization-form');
        localizationForm?.hidePanel?.();
      }

      close() {
        this.details.removeAttribute('open');
        this.summary.setAttribute('aria-expanded', 'false');
        if (this.header) this.header.preventHide = false;
        document.removeEventListener('click', this.onDocumentClick);
        this.closeInnerCurrencyPanel();
      }

      observeTranscyLanguage() {
        const mount = this.querySelector('.transcy-switcher-manual');
        const langEl = this.querySelector('[data-locale-lang]');
        if (!mount || !langEl) return;

        const sync = () => {
          const primary = mount.querySelector('.transcy-switcher-item-primary, #transcyBtnSwitcher');
          if (!primary) return;

          const text = (primary.textContent || '').trim();
          const match = text.match(/\b([A-Za-z]{2}(?:-[A-Za-z]{2})?)\b/);
          if (match) {
            langEl.textContent = match[1].split('-')[0].toUpperCase();
          }

          const flagImg = primary.querySelector('img.transcy-flag, img');
          if (!flagImg?.src) return;

          const triggerFlag = this.querySelector('[data-locale-flag]');
          if (triggerFlag) {
            triggerFlag.src = flagImg.src;
            triggerFlag.removeAttribute('data-flag-pending');
            triggerFlag.classList.remove('locale-switcher__flag--pending');
          }
        };

        sync();
        const observer = new MutationObserver(sync);
        observer.observe(mount, { childList: true, subtree: true, characterData: true });

        // If Transcy never injects, reveal the Liquid language-mapped flag instead of staying blank
        window.setTimeout(() => {
          const triggerFlag = this.querySelector('[data-locale-flag]');
          if (!triggerFlag?.hasAttribute('data-flag-pending')) return;
          triggerFlag.removeAttribute('data-flag-pending');
          triggerFlag.classList.remove('locale-switcher__flag--pending');
        }, 2500);
      }
    }
  );
}
